/* eslint-disable jsx-a11y/anchor-is-valid */
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import { Box, Stack, useTheme } from "@mui/material";
import { resetURL } from "utils/urls";
import { q0Enum, RoadCoveringEnum } from "types";
import { Header } from "components/Header";
import { StepList } from "components/StepList";
import { Loader } from "components/Loader";
import { Sidebar } from "components/Sidebar";
import { Navigation } from "components/Navigation";
import { ProjectManager } from "components/ProjectManager";
import { STEPS, StreeCalcDefaultConfig } from "./consts";
import { Family, RoadLayout, RoadWay, LightingClass, LuminaireLayout, Optimization, Result, SelectResult } from "./steps";
import { useAppUI, useProject, useWizardConfig, useProjectPersistence, useFetchInitialData, useFetchTooltipData, useURLHydration } from "./hooks";
import getStyles from "./styles";

function App() {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage;
  const theme = useTheme();
  const styles = getStyles(theme);

  // UI state (menu, language)
  const { showMenu, openMenu, closeMenu, changeLanguage } = useAppUI();

  // Project state (portfolio, project config)
  const { portfolioId, setPortfolioId, projectConfig, setProjectConfig, updateProject } = useProject();

  // Wizard configs (all step configs + result selection + navigation + validation)
  const {
    familyConfig,
    setFamilyConfig,
    roadLayoutConfig,
    setRoadLayoutConfig,
    roadwayConfig,
    setRoadwayConfig,
    lightingClassConfig,
    setLightingClassConfig,
    luminaireLayoutConfig,
    setLuminaireLayoutConfig,
    optimizationConfig,
    setOptimizationConfig,
    selectedResult,
    setSelectedResult,
    resultFamilyIndex,
    setResultFamilyIndex,
    handleSelectResult,
    currentStep,
    setCurrentStep,
    configIsValid,
  } = useWizardConfig();

  // URL hydration
  useURLHydration(setRoadLayoutConfig, setRoadwayConfig, setLightingClassConfig, setLuminaireLayoutConfig, setOptimizationConfig, setProjectConfig);

  // Data fetching
  const { data, error: fetchFamilyError, loading, refetch } = useFetchInitialData(portfolioId);
  const families = { data };

  const { data: tooltipData } = useFetchTooltipData();
  const tooltipPerLanguage = tooltipData?.[language as keyof typeof tooltipData] ?? null;

  // Project persistence (save/load)
  const { saveProject, openProject } = useProjectPersistence({
    portfolioId,
    currentStep,
    familyConfig,
    roadLayoutConfig,
    roadwayConfig,
    lightingClassConfig,
    luminaireLayoutConfig,
    optimizationConfig,
    projectConfig,
    selectedResult,
    setPortfolioId,
    setCurrentStep,
    setFamilyConfig,
    setRoadLayoutConfig,
    setRoadwayConfig,
    setLightingClassConfig,
    setLuminaireLayoutConfig,
    setOptimizationConfig,
    setProjectConfig,
    setSelectedResult,
    refetch,
  });

  if (fetchFamilyError) {
    console.error(fetchFamilyError);
  }

  if (loading || !data) {
    return <Loader />;
  }

  const stepsListProps = {
    closeMenu,
    resetSelected: setSelectedResult,
    setCurrentStep,
    validationCallback: configIsValid,
    currentStep,
  };

  const handleReset = () => {
    setProjectConfig({ project_id: "", name_of_road: "", project_name: "", location: "", comment: "" });
    setFamilyConfig({ familyIds: [] });
    setRoadLayoutConfig({ ...StreeCalcDefaultConfig.roadLayoutConfig });
    setRoadwayConfig({
      ...StreeCalcDefaultConfig.roadwayConfig,
      covering: language === "fr" ? RoadCoveringEnum.R2 : RoadCoveringEnum.R3,
      q0: language === "fr" ? q0Enum.q0007 : q0Enum.q0008,
    });
    setLightingClassConfig({ ...StreeCalcDefaultConfig.lightingClassConfig });
    setLuminaireLayoutConfig({ ...StreeCalcDefaultConfig.luminaireLayoutConfig });
    setOptimizationConfig({
      ...cloneDeep(StreeCalcDefaultConfig.optimizationConfig),
      maintenanceFactor: language === "fr" ? 0.9 : StreeCalcDefaultConfig.optimizationConfig.maintenanceFactor,
    });
    setResultFamilyIndex(-1);
    resetURL();
    setCurrentStep(1);
  };

  const sharedNav = (previous?: string, next?: string) => (
    <Navigation previous={previous} next={next} setCurrentStep={setCurrentStep} validationCallback={configIsValid} />
  );

  return (
    <Stack sx={{ flexDirection: "row" }}>
      <ProjectManager saveProject={saveProject} openProject={openProject} />

      <Sidebar>
        <StepList {...stepsListProps} />
      </Sidebar>

      <Box component="main" sx={styles.content}>
        <Header showMenu={showMenu} openMenu={openMenu} closeMenu={closeMenu} changeLanguage={changeLanguage} onReset={handleReset}>
          <StepList {...stepsListProps} />
        </Header>

        {currentStep === 1 && (
          <>
            <Family families={families} familyConfig={familyConfig?.familyIds} familyConfigCallback={setFamilyConfig} />
            {sharedNav(undefined, STEPS.ROAD_LAYOUT.en)}
          </>
        )}

        {currentStep === 2 && (
          <>
            <RoadLayout
              roadLayoutConfig={roadLayoutConfig}
              luminaireLayoutConfig={luminaireLayoutConfig}
              onRoadLayoutConfig={setRoadLayoutConfig}
              onLuminaireLayoutConfig={setLuminaireLayoutConfig}
            />
            {sharedNav(STEPS.FAMILY.en, STEPS.ROADWAY.en)}
          </>
        )}

        {currentStep === 3 && (
          <>
            <RoadWay
              roadLayoutConfig={roadLayoutConfig}
              roadwayConfig={roadwayConfig}
              onRoadwayConfig={setRoadwayConfig}
              optimizationConfig={optimizationConfig}
              onOptimizationConfig={setOptimizationConfig}
              tooltips={tooltipPerLanguage}
            />
            {sharedNav(STEPS.ROAD_LAYOUT.en, STEPS.LIGHTING_CLASS.en)}
          </>
        )}

        {currentStep === 4 && (
          <>
            <LightingClass
              roadLayoutConfig={roadLayoutConfig}
              lightingClassConfig={lightingClassConfig}
              onLightingClassConfig={setLightingClassConfig}
              tooltips={tooltipPerLanguage}
            />
            {sharedNav(STEPS.ROADWAY.en, STEPS.LUMINAIRE_LAYOUT.en)}
          </>
        )}

        {currentStep === 5 && (
          <>
            <LuminaireLayout
              roadLayoutConfig={roadLayoutConfig}
              luminaireLayoutConfig={luminaireLayoutConfig}
              onLuminaireLayoutConfig={setLuminaireLayoutConfig}
            />
            {sharedNav(STEPS.LIGHTING_CLASS.en, STEPS.OPTIMIZATION.en)}
          </>
        )}

        {currentStep === 6 && (
          <>
            <Optimization
              luminaireLayoutConfig={luminaireLayoutConfig}
              optimizationConfig={optimizationConfig}
              optimizationConfigCallBack={setOptimizationConfig}
              roadwayConfig={roadwayConfig}
              lightingClassConfig={lightingClassConfig}
              tooltips={tooltipPerLanguage}
            />
            {sharedNav(STEPS.LUMINAIRE_LAYOUT.en, STEPS.RESULT_SELECTION.en)}
          </>
        )}

        {currentStep === 7 && (
          <>
            <SelectResult
              families={families}
              familyConfig={familyConfig}
              roadLayoutConfig={roadLayoutConfig}
              lightingClassConfig={lightingClassConfig}
              roadwayConfig={roadwayConfig}
              luminaireLayoutConfig={luminaireLayoutConfig}
              optimizationConfig={optimizationConfig}
              selectedResult={selectedResult}
              handleSelectResult={handleSelectResult}
              resultFamilyIndex={resultFamilyIndex}
              setResultFamilyIndex={setResultFamilyIndex}
            />
            {sharedNav(STEPS.OPTIMIZATION.en, STEPS.RESULT.en)}
          </>
        )}

        {currentStep === 8 && (
          <>
            <Result
              families={families}
              familyConfig={familyConfig}
              roadLayoutConfig={roadLayoutConfig}
              lightingClassConfig={lightingClassConfig}
              roadwayConfig={roadwayConfig}
              luminaireLayoutConfig={luminaireLayoutConfig}
              projectConfig={projectConfig}
              optimizationConfig={optimizationConfig}
              selectedResult={selectedResult}
              updateProject={updateProject}
              resultFamilyIndex={resultFamilyIndex}
            />
            {sharedNav(STEPS.RESULT_SELECTION.en)}
          </>
        )}
      </Box>
    </Stack>
  );
}

export default App;
